import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

import { login as apiLogin, verifyToken } from './api';

export type AuthContextType = {
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkToken = async () => {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setIsAuthenticated(false);
        setLoading(false);
        return;
      }
      const valid = await verifyToken(token);
      setIsAuthenticated(valid);
      if (!valid) localStorage.removeItem('authToken');
      setLoading(false);
    };
    checkToken();
  }, []);

  const handleLogin = async (username: string, password: string) => {
    setLoading(true);
    try {
      const token = await apiLogin(username, password);
      localStorage.setItem('authToken', token);
      setIsAuthenticated(true);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login: handleLogin, logout: handleLogout, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
